import * as Sentry from "@sentry/browser";
import { useCallback, useEffect, useRef, useState } from "react";
import { ErrorResponse, UnauthorizedResponse } from "~services/app/standardResponsesTypes";

import api, { EndpointsWithoutData, EndpointsWithRequiredData } from "../api-client";
import { makeApiErrorEvent } from "../api-errors";
import { ApiRequests, ApiResponses, Successful, Unsuccessful } from "../data/api-endpoints";
import { useApiCache } from "../providers/ApiCacheProvider";
import { useToast } from "../providers/ToastProvider";
import { loadRecaptchaScript, REQUIRED_CAPTCHA_ENDPOINTS } from "../utils/recaptcha";
import useCachedObject, { useCachedState } from "./cached-object";

export type ApiDataOf<T extends keyof ApiRequests> = UseApiDataReturnType<T>;

type EndpointsThatAlwaysSucceed = {
  [K in keyof ApiResponses]: [Unsuccessful<ApiResponses[K]>] extends [never] ? K : never;
}[keyof ApiResponses];

// Endpoints where the only possible error is "record_not_found"
type EndpointsWithOnlyRecordNotFoundError = {
  [K in keyof ApiResponses]: [Unsuccessful<ApiResponses[K]>] extends [never]
    ? never
    : Unsuccessful<ApiResponses[K]> extends { error: infer E }
      ? [E] extends ["record_not_found"]
        ? K
        : never
      : never;
}[keyof ApiResponses];

// No idea why this is required, but without it makeSetter becomes "too complex"
type TypeFixApiResponses = ApiResponses & object;

type UseApiDataBaseOptions = {
  enable?: boolean;
  refreshStale?: boolean;
};

// onError is required for endpoints that can fail, UNLESS the only error is "record_not_found"
type UseApiDataOptions<T extends keyof ApiResponses> = ([Unsuccessful<ApiResponses[T]>] extends [never]
  ? { onError?: (response: Unsuccessful<ApiResponses[T]>) => void }
  : T extends EndpointsWithOnlyRecordNotFoundError
    ? { onError?: (response: Unsuccessful<ApiResponses[T]>) => void }
    : { onError: (response: Unsuccessful<ApiResponses[T]>) => void }) &
  UseApiDataBaseOptions;

type UseApiDataReturnType<T extends keyof TypeFixApiResponses> = {
  makeSetter: <K extends keyof Successful<TypeFixApiResponses[T]>>(
    key: K,
    clear?: Array<keyof ApiRequests>,
  ) => SetterFor<T, K>;
  refresh: (clear?: boolean) => void;
} & (
  | (Partial<Successful<TypeFixApiResponses[T]>> & { isLoading: true; isRefreshing: true })
  | (Successful<TypeFixApiResponses[T]> & { isLoading: false; isRefreshing: boolean })
);

const promises = new Map<string, Promise<any>>();

export type SetterFor<T extends keyof TypeFixApiResponses, K extends keyof Successful<TypeFixApiResponses[T]>> = (
  setter: (prev: Successful<TypeFixApiResponses[T]>[K]) => Successful<TypeFixApiResponses[T]>[K],
) => void;

// Endpoints where options is optional (always succeed OR only error is record_not_found)
type EndpointsWithOptionalOnError = EndpointsThatAlwaysSucceed | EndpointsWithOnlyRecordNotFoundError;
type UseApiReturnType<
  T extends keyof ApiRequests,
  D extends Partial<ApiRequests[T]> | undefined,
> = (ApiRequests[T] extends undefined
  ? {
      call: (data?: undefined) => Promise<ApiResponses[T]>;
    }
  : D extends undefined
    ? {
        call: (data: ApiRequests[T]) => Promise<ApiResponses[T]>;
      }
    : keyof Omit<ApiRequests[T], keyof D> extends never
      ? {
          call: (data?: Omit<ApiRequests[T], keyof D>) => Promise<ApiResponses[T]>;
        }
      : {
          call: (data: Omit<ApiRequests[T], keyof D>) => Promise<ApiResponses[T]>;
        }) & {
  isLoading: boolean;
};
export function useApi<T extends keyof ApiRequests>(endpoint: T): UseApiReturnType<T, undefined>;

export function useApi<T extends keyof ApiRequests>(endpoint: T): UseApiReturnType<T, undefined>;

export function useApi<T extends keyof ApiRequests, D extends Partial<ApiRequests[T]>>(
  endpoint: T,
  defaultData?: D,
  invalidates?: Array<keyof ApiRequests>,
): UseApiReturnType<T, D>;

export function useApi<T extends keyof ApiRequests, D extends Partial<ApiRequests[T]>>(
  endpoint: T,
  defaultData?: D,
  invalidates?: Array<keyof ApiRequests>,
): UseApiReturnType<T, D> {
  const [isLoading, setIsLoading] = useState(false);
  const { forgetCache, invalidateCache } = useApiCache();
  // Ensure the apiEndpoint doesn't change
  const apiEndpoint = useRef(endpoint);
  if ((apiEndpoint.current as string) !== (endpoint as string)) throw new Error("apiEndpoint cannot change");

  const cachedDefaultData = useCachedObject(defaultData);
  const cachedInvalidates = useCachedObject(invalidates);

  useEffect(() => {
    if (REQUIRED_CAPTCHA_ENDPOINTS.includes(endpoint)) void loadRecaptchaScript();
  }, [endpoint]);

  const call = useCallback(
    async (data?: Omit<ApiRequests[T], keyof D>): Promise<ApiResponses[T]> => {
      setIsLoading(true);
      try {
        return await api(endpoint as any, { ...cachedDefaultData, ...data });
      } finally {
        let responseAt = new Date();
        setTimeout(() => {
          setIsLoading(false);
        }, 0); // In case we update state in the same tick
        setTimeout(() => {
          invalidateCache(
            responseAt,
            Object.entries({ ...cachedDefaultData, ...data })
              .filter(([key, value]: [string, unknown]) => key.endsWith("Id") && typeof value === "string")
              .map(([key, value]) => value),
          );
          cachedInvalidates?.forEach((key) => {
            forgetCache(key);
          });
        }, 100);
      }
    },
    [cachedDefaultData, endpoint, invalidateCache, forgetCache, cachedInvalidates],
  );

  return { call, isLoading } as unknown as UseApiReturnType<T, D>;
}

export function useApiData<T extends EndpointsWithOptionalOnError & EndpointsWithoutData>(
  endpoint: T,
  data?: undefined,
  options?: UseApiDataOptions<T>,
): UseApiDataReturnType<T>;
export function useApiData<T extends EndpointsWithOptionalOnError & EndpointsWithRequiredData>(
  endpoint: T,
  data: ApiRequests[T],
  options?: UseApiDataOptions<T>,
): UseApiDataReturnType<T>;
// Endpoints where options with onError is required (can fail with errors other than just record_not_found)
export function useApiData<T extends EndpointsWithoutData>(
  endpoint: T,
  data: undefined,
  options: UseApiDataOptions<T>,
): UseApiDataReturnType<T>;
export function useApiData<T extends EndpointsWithRequiredData>(
  endpoint: T,
  data: ApiRequests[T], // eslint-disable-line  @typescript-eslint/unified-signatures
  options: UseApiDataOptions<T>,
): UseApiDataReturnType<T>;
export function useApiData<T extends keyof ApiRequests>(
  endpoint: T,
  data?: ApiRequests[T],
  options?: UseApiDataOptions<T>,
): UseApiDataReturnType<T> {
  // Ensure the apiEndpoint doesn't change
  const apiEndpoint = useRef(endpoint);
  if ((apiEndpoint.current as string) !== (endpoint as string))
    throw new Error("apiEndpoint cannot change (changed from " + apiEndpoint.current + " to " + endpoint + ")");

  const { forgetCache, getCache, setCache } = useApiCache();
  const addToast = useToast();
  const [response, setResponse] = useCachedState<null | Successful<ApiResponses[T]>>(null);
  const [everLoaded, setEverLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const settersCache = useRef<Map<string, Function>>(new Map());
  const cachedData = useCachedObject(data);
  const isLoadingRef = useRef(isLoading);
  const responseRef = useRef(response);
  const optionsRef = useRef(options);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  useEffect(() => {
    responseRef.current = response;
  }, [response]);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const enabled = options?.enable ?? true;
  const refreshStale = options?.refreshStale ?? true;
  const expectResponseChange = useRef(true);
  if (!response) expectResponseChange.current = true;

  useEffect(() => {
    if (!response) return;
    if (expectResponseChange.current) {
      expectResponseChange.current = false;
      return;
    }
    if (process.env.NODE_ENV === "development") {
      addToast({
        message: `useApiData("${endpoint}"); has changed response without a call for refresh.`,
        title: "DATA CHANGE ALERT",
      });
    }
  }, [addToast, response, endpoint]);

  const storeCache = useCallback(
    (endpoint: T, data: ApiRequests[T] | undefined, response: ApiResponses[T]) => {
      if (response.success) setCache(endpoint, data, response);
    },
    [setCache],
  );

  const refresh = useCallback(
    (clear?: boolean) => {
      if (clear) {
        setResponse(null);
        responseRef.current = null;
        setEverLoaded(false);
        forgetCache(endpoint, cachedData, true);
      }
      if (isLoadingRef.current) return; // Ignore refresh if a request is already happening

      setIsLoading(true);
      const promiseKey = JSON.stringify({ data: cachedData, endpoint });
      const promise = promises.get(promiseKey) ?? api(endpoint as any, cachedData);
      const requestedData = cachedData;
      promises.set(promiseKey, promise);
      void promise.then((response: ApiResponses[T]) => {
        promises.delete(promiseKey);
        storeCache(endpoint, cachedData, response);
        if (cachedData !== requestedData) return; // This response is no longer relevant

        if (response.success) {
          setResponse(response as any); // as Successful<ApiResponses[T]>;
          setIsLoading(false);
          setEverLoaded(true);
        } else if (optionsRef.current?.onError) {
          (optionsRef.current.onError as (response: ErrorResponse | UnauthorizedResponse) => void)(response); // as Unsuccessful<ApiResponses[T]>;
        } else if (response.error === "record_not_found") {
          document.dispatchEvent(makeApiErrorEvent("record_not_found"));
        } else {
          const error = `useApiData has returned an error in ${endpoint} (${response.error}) but there is no onError handler registered`;
          if (process.env.NODE_ENV === "development") alert(error);
          else Sentry.captureException(new Error(error));
        }
      });
      void promise.catch(() => {
        promises.delete(promiseKey);
        if (responseRef.current) setIsLoading(false);
        else document.dispatchEvent(makeApiErrorEvent("unexpected"));
      });
    },
    [cachedData, endpoint, storeCache, setResponse, forgetCache],
  );

  useEffect(() => {
    if (!enabled) return;

    const [status, response] = getCache(endpoint, cachedData);
    if (response) {
      if (status !== "fresh" && refreshStale) refresh();
      if (status === "cleared") expectResponseChange.current = true;

      setResponse(response);
      setIsLoading(false);
      setEverLoaded(true);
    } else {
      refresh(true);
    }
  }, [endpoint, cachedData, getCache, refresh, enabled, setResponse, refreshStale]);

  const makeSetter = useCallback(
    <K extends keyof Successful<ApiResponses[T]>>(key: K, forget?: Array<keyof ApiRequests>) => {
      // Generate a unique identifier for the cache key
      const forgetKey = forget ? forget.join(",") : "";
      const cacheKey = `${String(key)}-${forgetKey}`;

      // Check if the setter function for this cacheKey is already cached
      if (settersCache.current.has(cacheKey)) return settersCache.current.get(cacheKey);

      // Create a new setter function
      const newSetter = (func: (prevValue: Successful<ApiResponses[T]>[K]) => Successful<ApiResponses[T]>[K]) => {
        const response = responseRef.current;
        if (response?.success) {
          response[key] = func(response[key]);
          expectResponseChange.current = true;
          setResponse(response);
          storeCache(endpoint, cachedData, response as any);
        }
        if (forget) for (const key of forget) forgetCache(key);
      };

      // Cache and return the new setter function
      settersCache.current.set(cacheKey, newSetter);
      return newSetter;
    },
    [endpoint, forgetCache, storeCache, cachedData, setResponse],
  );

  return {
    ...response,
    isLoading: !everLoaded,
    isRefreshing: isLoading,
    makeSetter,
    refresh,
  } as UseApiDataReturnType<T>;
}
